import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/axios";
import Header from "../components/ui/Header";
import Sidebar from "../components/ui/Sidebar";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";

const fields = [
  ["production", "Production"],
  ["qualite", "Qualité"],
  ["logistique", "Logistique"],
  ["transit", "Transit"],
  ["rh", "RH"],
  ["finance_comptabilite", "Finance-Comptabilité"],
  ["cao", "CAO"],
  ["chauffeur", "Chauffeur"],
  ["electricien", "Electricien"],
  ["forgeron", "Forgeron"],
  ["gardiennage", "Gardiennage"],
  ["mecaniciens", "Mecaniciens"],
  ["menage", "Menage"],
  ["methode", "Methode"],
  ["autres", "Autres"],
];

const emptyValues = Object.fromEntries(fields.map(([name]) => [name, ""]));
const today = new Date().toISOString().split("T")[0];

const RealEffectiveFixed = () => {
  const navigate = useNavigate();
  const [sidebar, setSidebar] = useState(false);
  const [date, setDate] = useState(today);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [existing, setExisting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    if (!token) {
      navigate("/");
    } else if (!["admin", "superadmin", "developer", "RH"].includes(role)) {
      navigate("/dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    if (!date) {
      setValues(emptyValues);
      setExisting(false);
      return;
    }

    let active = true;
    setFetching(true);
    api.get("/effectif-reel-fixe", { params: { date } })
      .then(({ data }) => {
        if (!active) return;
        if (data) {
          setValues(Object.fromEntries(fields.map(([name]) => [name, data[name] ?? ""])));
          setExisting(true);
        } else {
          setValues(emptyValues);
          setExisting(false);
        }
      })
      .catch(() => active && toast.error("Impossible de charger les données."))
      .finally(() => active && setFetching(false));

    return () => { active = false; };
  }, [date]);

  const total = useMemo(
    () => fields.reduce((sum, [name]) => sum + (Number(values[name]) || 0), 0),
    [values]
  );

  const handleChange = (name, value) => {
    if (value === "" || (/^\d+$/.test(value) && Number(value) >= 0)) {
      setValues((current) => ({ ...current, [name]: value }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!date) {
      toast.error("Veuillez sélectionner une date.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/effectif-reel-fixe", { date, ...values });
      setExisting(true);
      toast.success(existing ? "Effectif réel fixe mis à jour." : "Effectif réel fixe enregistré.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header sidebar={sidebar} setSidebar={setSidebar} />
      <Sidebar sidebar={sidebar} setSidebar={setSidebar} />
      <main className="pt-24 pb-12 px-4 lg:ml-[16.666667%]">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6 md:p-8">
          <h1 className="text-2xl font-bold text-center mb-8">Effectif Réel Fixe</h1>

          <div className="mb-6">
            <label htmlFor="effectif-fixe-date" className="block font-semibold">Date</label>
            <input
              id="effectif-fixe-date"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              required
              className="block w-full mt-4 outline-0 p-2 border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded"
            />
          </div>

          {fetching ? (
            <p className="text-center py-8">Chargement...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                {fields.map(([name, label]) => (
                  <Input
                    key={name}
                    container="mb-4"
                    label={label}
                    type="number"
                    name={name}
                    text={values[name]}
                    handleChange={handleChange}
                  />
                ))}
              </div>

              <div className="my-6 rounded-lg bg-blue-50 border border-blue-200 px-5 py-4 flex justify-between items-center">
                <span className="font-semibold text-gray-700">Total</span>
                <span className="text-2xl font-bold text-blue-700">{total}</span>
              </div>

              <Button classes="bg-blue-500 my-5" >
                {loading ? "Enregistrement..." : existing ? "Mettre à jour" : "Enregistrer"}
              </Button>
            </>
          )}
        </form>
      </main>
    </div>
  );
};

export default RealEffectiveFixed;
